import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BarraDeLista, BuscaDaBarra, FiltrosAtivos, type FiltroAtivo } from '../../BarraDeLista';
import { useCaminho } from '../../Caminho';
import { Seletor } from '../../Filtros';
import { FolhaDeBaixo } from '../../FolhaDeBaixo';
import { data, horaCurta, nomeDoContato } from '../../formato';
import { useSessao } from '../../sessao';
import { degrauDe, SITUACAO_DO_ATENDIMENTO } from '../../situacoes';
import { AtualizacaoPausada, useTempoReal } from '../../tempoReal';
import { Aviso, Carregando, ErroDaApi, Selo, SeloDaEscada, useEstreito } from '../../ui';
import { api } from '../../api';
import {
  Conversa, passaA, primeiroNome, semRespostaHa, situacaoDoAtendimento,
  type DetalheDaConversa, type ItemDaCaixa,
} from './Conversa';
import { FotoDoContato } from './FotoDoContato';
import { JanelaDaLigacao } from './JanelasDaConversa';
import { JanelaNovaConversa, type InicialDaNovaConversa } from './JanelaNovaConversa';
import { PainelDaConversa } from './PainelDaConversa';
import './comercial.css';

/**
 * A caixa de entrada do Comercial — onda 550 (Plano 2 · Comercial, Etapa 1,
 * Tarefa 14; spec do Comercial §8.1). É a primeira tela do Comercial, e
 * com ela o espaço abre (0507).
 *
 * ─── A tela ───────────────────────────────────────────────────────────
 *
 * Uma tela só para os dois endereços: `/comercial/caixa` (a lista) e
 * `/comercial/conversa/:id` (a lista, a conversa e o painel do cliente). O
 * endereço diz qual conversa está aberta — F5 e «voltar» a mantêm, e o
 * link da conversa vai num aviso (`conversa_para_voce`).
 *
 *  · ≥ 1100 px: três colunas — a lista, a conversa e o painel do cliente;
 *  · 641–1099 px: a lista e a conversa; o painel mora na folha que sobe
 *    (o botão «Cliente e oportunidade» no cabeçalho da conversa);
 *  · ≤ 640 px: uma de cada vez — a lista, ou a conversa com o «voltar».
 *
 * ─── O cabeçalho compacto (P-LST-15, decisão do Humberto de 23/10/2026) ─
 *
 * A caixa é a exceção declarada ao padrão de lista com ficha (P-LST-07): o
 * espaço vai para a conversa. O cabeçalho é UMA fileira — o título, os
 * cinco recortes como abas da lista (`.abas-lista`, Minhas, Sem dono, Com o
 * agente, Aguardando cliente, Todas — um vale por vez, e cada um diz
 * quantas conversas tem) e, à direita, «Registrar ligação» e «Nova conversa»
 * (onda 554, Tarefa 7: a equipe começa a conversa de WhatsApp); sem a frase
 * da ordem e sem os cartões do resumo. A busca, o canal e a situação
 * (`BarraDeLista`, `Seletor`) moram no topo da coluna da lista. Os recortes
 * e a busca filtram aqui, sobre a lista que a API devolve para o canal e a
 * situação; tudo vai no endereço (`?rec=`, `?canal=`, `?situacao=`,
 * P-LST-05). A ordem é a da API: a prioridade, o tempo sem resposta, a
 * atividade. Sem rodapé de paginação: a coluna rola por dentro. A lista
 * vem da API já no recorte, no canal, na situação e na busca (que corre no
 * banco, sem acento), com um teto de 500 (`LIMITE_DA_LISTA`); quando o teto
 * é alcançado, o pé da lista diz isso e como achar as outras (CA-28). As
 * contas das abas vêm de `/comercial/conversas/contagem`, sem teto.
 *
 * Cada linha diz de quem é a vez (`SeloDaEscada`, P-UI-41), há quanto tempo o cliente espera
 * («sem resposta há 1 h 20») e quando a conversa passa ao substituto
 * («passa a Fabio às 14:32»): a regra do substituto à vista, e não escondida
 * num relógio do servidor. O tempo anda sozinho a cada 30 s.
 *
 * ─── Ao vivo ──────────────────────────────────────────────────────────
 *
 * `useTempoReal(['comercial.caixa'])` — a lista busca de novo a cada aviso
 * (só ids) e no `{ recarregar: true }`. O sino da moldura e a conversa
 * aberta assinam os tópicos deles no MESMO fluxo da aba (um gerenciador por
 * aba, `tempoReal.ts`).
 */

type Filtro = 'minhas' | 'sem_dono' | 'com_agente' | 'aguardando_cliente' | 'todas';

const FILTROS: { chave: Filtro; texto: string }[] = [
  { chave: 'minhas', texto: 'Minhas' },
  { chave: 'sem_dono', texto: 'Sem dono' },
  { chave: 'com_agente', texto: 'Com o agente' },
  { chave: 'aguardando_cliente', texto: 'Aguardando cliente' },
  { chave: 'todas', texto: 'Todas' },
];

/** O teto da lista na API (`LIMITE_DA_LISTA`, `conversas.service.ts`). */
const LIMITE_DA_CAIXA = 500;

/**
 * O recorte, com a mesma regra da API (`FILTRO_SQL`, Tarefa 13): a lista
 * já vem recortada, e o filtro aqui só segura a troca de aba até a
 * resposta chegar.
 */
function noRecorte(c: ItemDaCaixa, f: Filtro, eu: string): boolean {
  switch (f) {
    case 'minhas': return c.dono.tipo === 'pessoa' && c.dono.id === eu;
    case 'sem_dono': return !c.assumidaEm && c.dono.tipo !== 'agente';
    case 'com_agente': return c.dono.tipo === 'agente';
    case 'aguardando_cliente': return c.situacao === 'aguardando_cliente';
    default: return true;
  }
}

const semAcento = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

interface Canal { codigo: string; nome: string; tipo: string; situacao: string }

/** A prévia da última mensagem, dita por extenso quando não é texto (P-TXT-02). */
function previa(c: ItemDaCaixa): string {
  const u = c.ultimaMensagem;
  if (!u) return 'sem mensagens ainda';
  const quem = u.direcao === 'enviada' ? 'Você: ' : u.direcao === 'interna' ? 'Nota: ' : '';
  /* A mensagem comercial, para quem não tem o Comercial (Tarefa 16b): a API não manda o texto. */
  if (u.oculta) return `${quem}Mensagem comercial oculta`;
  if (u.texto) return `${quem}${u.texto}`;
  const tipo: { readonly [t: string]: string } = {
    audio: 'áudio', imagem: 'imagem', arquivo: 'anexo', ligacao: 'ligação registrada',
    /* Onda 555 (A-1). */
    video: 'vídeo', figurinha: 'figurinha', localizacao: 'localização', contato: 'cartão de contato',
  };
  return `${quem}${tipo[u.tipo ?? ''] ?? 'mensagem sem texto'}`;
}

/** A hora da última atividade: hoje, a hora; antes, o dia. */
function quando(iso: string | undefined): string {
  if (!iso) return '';
  return data(iso) === data(new Date()) ? horaCurta(iso) : data(iso).slice(0, 5);
}

function LinhaDaCaixa({ c, aberta, decorrido }: { c: ItemDaCaixa; aberta: boolean; decorrido: number }) {
  const sit = degrauDe(SITUACAO_DO_ATENDIMENTO, situacaoDoAtendimento(c));
  const minutos = c.minutosSemResposta === null ? null : c.minutosSemResposta + decorrido;
  const tempo = semRespostaHa(minutos);
  const prazo = passaA(c.substituto, c.prazoSubstituto);
  const dono = c.dono.tipo === 'pessoa' && c.dono.nome ? primeiroNome(c.dono.nome) : null;
  return (
    <li className={`caixa-item${aberta ? ' aberta' : ''}${c.prioridade ? ' prioridade' : ''}`}>
      <Link to={`/comercial/conversa/${c.id}`} className="caixa-item-link" aria-current={aberta ? 'true' : undefined}>
        {/* A foto do WhatsApp, ou as iniciais (onda 555, §3.1). */}
        <FotoDoContato contatoId={c.contato.id} nome={nomeDoContato(c.contato.nome)} temFoto={c.contato.temFoto === true} tamanho="p" />
        <span className="caixa-item-corpo">
        <span className="caixa-item-topo">
          <span className="caixa-nome">{nomeDoContato(c.contato.nome)}</span>
          <time className="caixa-hora" dateTime={c.ultimaMensagem?.criadaEm}>{quando(c.ultimaMensagem?.criadaEm)}</time>
        </span>
        <span className="caixa-previa">{previa(c)}</span>
        <span className="caixa-meta">
          {c.prioridade && <Selo tom="critico">Prioridade</Selo>}
          <SeloDaEscada degrau={sit} />
          <span className="caixa-canal">{c.canal.nome}{dono ? ` · ${dono}` : ''}</span>
        </span>
        {(tempo || prazo) && (
          <span className="caixa-relogio">
            {tempo && <span className={`caixa-tempo${minutos !== null && minutos >= 60 ? ' demorado' : ''}`}>{tempo}</span>}
            {prazo && <span className="caixa-prazo">{prazo}</span>}
          </span>
        )}
        </span>
      </Link>
    </li>
  );
}

export function Caixa() {
  const { id } = useParams<{ id?: string }>();
  const { eu, pode } = useSessao();
  const euId = eu?.usuarioId ?? '';
  const navegar = useNavigate();
  const [busca, setBusca] = useSearchParams();
  /* O recorte (`rec=`, P-LST-05): sem ele, «Minhas». */
  const filtro = (FILTROS.some((f) => f.chave === busca.get('rec')) ? busca.get('rec') : 'minhas') as Filtro;
  const canal = busca.get('canal');
  const situacao = busca.get('situacao') === 'encerrada' ? 'encerrada' : null;
  const [texto, setTexto] = useState('');
  /* A busca que vai à API: o texto, 300 ms depois de a pessoa parar. */
  const [termo, setTermo] = useState('');
  useEffect(() => {
    const t = window.setTimeout(() => setTermo(texto.trim()), 300);
    return () => window.clearTimeout(t);
  }, [texto]);

  const celular = useEstreito(640);
  const lateralNaFolha = useEstreito(1099);
  const [folha, setFolha] = useState(false);
  const [ligacao, setLigacao] = useState(false);
  const [novaConversa, setNovaConversa] = useState(false);
  /* A Nova conversa aberta pela ligação gravada com «Chamar no WhatsApp depois de gravar» (Tarefa 8). */
  const [inicialDaNova, setInicialDaNova] = useState<InicialDaNovaConversa | undefined>(undefined);
  /* O painel mudou a conversa (ligou outra oportunidade, abriu uma, gravou o contato): a conversa busca de novo. */
  const [versaoDoDetalhe, setVersaoDoDetalhe] = useState(0);
  /* A Nova conversa envia: é de quem responde (`POST /comercial/conversas`). */
  const responde = pode('atendimento.responder');

  const [lista, setLista] = useState<ItemDaCaixa[] | null>(null);
  /* A busca com que a lista que está na tela foi pedida ao banco. */
  const [termoDaLista, setTermoDaLista] = useState('');
  const [contagem, setContagem] = useState<Record<Filtro, number> | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [canais, setCanais] = useState<Canal[]>([]);
  const [detalhe, setDetalhe] = useState<DetalheDaConversa | null>(null);
  const [buscadoEm, setBuscadoEm] = useState(() => Date.now());
  const [agora, setAgora] = useState(() => Date.now());
  const pedido = useRef(0);

  /*
   * A trilha (P-UI-23): no celular a conversa ocupa a tela, e a trilha da
   * moldura dá o «← Caixa de entrada» e o nome do contato. No PC a conversa
   * é um painel da mesma tela — a lista ao lado já diz onde se está —, e a
   * trilha de um elo só some.
   */
  useCaminho(celular && id && detalhe?.conversa.id === id ? [{ rotulo: nomeDoContato(detalhe.conversa.contato.nome) }] : null);

  /*
   * A altura da caixa: do lugar em que ela começa até o pé da janela.
   * Medida, e não escrita à mão: a fileira do cabeçalho quebra numa janela
   * estreita, e a conta fixa erraria. O pé generoso (o «Relatar» flutua no
   * canto e não pode cobrir o «Enviar») só vale quando a conversa é a última
   * coluna (641–1099 px); com o painel do cliente ao lado, o «Relatar» cai
   * sobre o painel, que rola por dentro e guarda o espaço no pé dele. No
   * celular a caixa tem a altura do conteúdo (uma coisa de cada vez).
   */
  const superficie = useRef<HTMLDivElement>(null);
  const [altura, setAltura] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (celular) { setAltura(null); return undefined; }
    const medir = () => {
      const el = superficie.current;
      if (!el) return;
      const topo = el.getBoundingClientRect().top + window.scrollY;
      const pe = id && lateralNaFolha ? 72 : 16;
      setAltura(Math.max(448, Math.floor(window.innerHeight - topo - pe)));
    };
    medir();
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, [celular, id, lateralNaFolha, lista === null]);

  const mudar = (chave: string, valor: string | null) => {
    const b = new URLSearchParams(busca);
    if (valor === null) b.delete(chave); else b.set(chave, valor);
    setBusca(b, { replace: true });
  };

  const buscar = useCallback(async () => {
    const n = ++pedido.current;
    const recorte = new URLSearchParams();
    if (canal) recorte.set('canal', canal);
    if (situacao) recorte.set('situacao', situacao);
    const q = new URLSearchParams(recorte);
    q.set('filtro', filtro);
    if (termo) q.set('busca', termo);
    try {
      const [r, c] = await Promise.all([
        api<ItemDaCaixa[]>(`/comercial/conversas?${q}`),
        api<Record<Filtro, number>>(`/comercial/conversas/contagem?${recorte}`),
      ]);
      if (n !== pedido.current) return;
      setLista(r);
      setTermoDaLista(termo);
      setContagem(c);
      setErro(null);
      setBuscadoEm(Date.now());
      setAgora(Date.now());
    } catch (e) {
      if (n === pedido.current) setErro(e);
    }
  }, [filtro, canal, situacao, termo]);

  useEffect(() => { void buscar(); }, [buscar]);
  const painelMudou = () => { setVersaoDoDetalhe((v) => v + 1); void buscar(); };
  useEffect(() => {
    api<Canal[]>('/comercial/canais').then(setCanais).catch(() => setCanais([]));
  }, []);
  /* O tempo sem resposta anda sozinho. */
  useEffect(() => {
    const t = window.setInterval(() => setAgora(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, []);

  const adiada = useRef<number | null>(null);
  const tempoReal = useTempoReal(['comercial.caixa'], () => {
    if (adiada.current !== null) return;
    adiada.current = window.setTimeout(() => { adiada.current = null; void buscar(); }, 250);
  });
  useEffect(() => () => { if (adiada.current !== null) window.clearTimeout(adiada.current); }, []);

  useEffect(() => { setFolha(false); if (!id) setDetalhe(null); }, [id]);

  /*
   * O filtro do navegador só segura o intervalo entre a tecla e a resposta:
   * enquanto a lista na tela é de outra busca, filtra por ela; quando o
   * banco responde à busca de agora, a resposta vale como veio — ele compara
   * a última mensagem inteira, e a prévia que chega aqui tem 160 letras (I-B
   * da revisão de 23/10/2026).
   */
  const visiveis = useMemo(() => {
    const t = texto.trim();
    const q = t !== termoDaLista ? semAcento(t) : '';
    return (lista ?? []).filter((c) => noRecorte(c, filtro, euId)
      && (!q || semAcento(`${c.contato.nome} ${c.ultimaMensagem?.texto ?? ''} ${c.canal.nome}`).includes(q)));
  }, [lista, termoDaLista, filtro, euId, texto]);

  /* O que filtra além do recorte (P-LST-05): removível, e «N de M». */
  const filtrosAtivos: FiltroAtivo[] = [
    ...(canal ? [{ chave: 'canal', texto: `Canal: ${canais.find((c) => c.codigo === canal)?.nome ?? canal}`,
      tirar: () => mudar('canal', null) }] : []),
    ...(situacao ? [{ chave: 'situacao', texto: 'Encerradas', tirar: () => mudar('situacao', null) }] : []),
    ...(texto.trim() ? [{ chave: 'busca', texto: `«${texto.trim()}»`, tirar: () => setTexto('') }] : []),
  ];

  const decorrido = Math.max(0, Math.floor((agora - buscadoEm) / 60_000));
  const semDono = contagem?.sem_dono ?? 0;
  /* A API parou no teto: a lista não está inteira, e o pé dela diz isso (CA-28). */
  const noTeto = !!lista && lista.length >= LIMITE_DA_CAIXA;
  const verLista = !celular || !id;
  const tresColunas = !!id && !lateralNaFolha && !celular;
  const ativosNaFolha = (canal ? 1 : 0) + (situacao ? 1 : 0);

  return (
    <div className={`caixa-tela${id ? ' com-conversa' : ''}`}>
      {verLista && (
        <>
          <header className="caixa-cabeca">
            <h1>Caixa de entrada</h1>
            <div className="abas-lista caixa-recortes" role="group" aria-label="Recortes da caixa de entrada">
              {FILTROS.map((f) => (
                <button key={f.chave} type="button" data-recorte={f.chave}
                  aria-pressed={filtro === f.chave} className={filtro === f.chave ? 'aqui' : undefined}
                  onClick={() => mudar('rec', f.chave === 'minhas' ? null : f.chave)}>
                  {f.texto}
                  <span className="aba-os-cont">{contagem ? contagem[f.chave] : 0}</span>
                </button>
              ))}
            </div>
            {!(id && lateralNaFolha) && (
              <div className="caixa-cabeca-acoes">
                <button type="button" className="botao secundario caixa-ligacao" onClick={() => setLigacao(true)}>
                  Registrar ligação
                </button>
                {/* A ação primária da caixa sem conversa aberta; com ela, a primária é o «Enviar» do compositor (P-UI-10). */}
                {responde && (
                  <button type="button" className={`botao${id ? ' secundario' : ''} caixa-nova-conversa`}
                    onClick={() => setNovaConversa(true)}>
                    Nova conversa
                  </button>
                )}
              </div>
            )}
          </header>
          <AtualizacaoPausada estado={tempoReal} />
        </>
      )}

      <div className="caixa-superficie" ref={superficie} data-colunas={tresColunas ? 3 : id && !celular ? 2 : 1}
        style={altura ? { height: `${altura}px` } : undefined}>
        {verLista && (
          <section className="caixa-coluna caixa-lista-coluna" aria-label="Conversas">
            <div className="caixa-filtros">
              <BarraDeLista rotulo="Filtros da caixa de entrada" ativos={ativosNaFolha} total={visiveis.length}
                aoLimpar={() => {
                  const b = new URLSearchParams(busca);
                  b.delete('canal'); b.delete('situacao');
                  setBusca(b, { replace: true });
                }}>
                <BuscaDaBarra valor={texto} aoMudar={setTexto} placeholder="Buscar"
                  rotulo="Buscar na caixa de entrada" />
                <Seletor naBarra rotulo="Canal" valor={canal} todos="Todos"
                  opcoes={canais.map((c) => ({ valor: c.codigo, texto: c.nome }))}
                  aoMudar={(v) => mudar('canal', v)} />
                <Seletor naBarra rotulo="Situação" valor={situacao} todos="Em aberto"
                  opcoes={[{ valor: 'encerrada', texto: 'Encerradas' }]}
                  aoMudar={(v) => mudar('situacao', v)} />
              </BarraDeLista>
              <FiltrosAtivos itens={filtrosAtivos} aoLimpar={() => { setTexto(''); setBusca(new URLSearchParams(), { replace: true }); }}
                contagem={texto.trim() && lista && contagem ? `${visiveis.length} de ${contagem[filtro]}` : undefined} />
            </div>
            {erro && !lista ? <ErroDaApi erro={erro} />
              : !lista ? <Carregando texto="Carregando as conversas" />
                : visiveis.length === 0 ? (
                  <div className="caixa-vazia">
                    <p><strong>Nenhuma conversa {filtro === 'todas' ? 'aqui' : `em «${FILTROS.find((f) => f.chave === filtro)!.texto}»`}.</strong></p>
                    <p className="sub">
                      {texto ? 'A busca não achou nada: tire a busca ou troque o recorte.'
                        : filtro === 'minhas' && semDono > 0
                          ? `Há ${semDono === 1 ? 'uma conversa' : `${semDono} conversas`} sem dono esperando alguém.`
                          : 'Quando um cliente escrever, a conversa aparece aqui.'}
                    </p>
                    {filtro === 'minhas' && semDono > 0 && (
                      <button type="button" className="botao secundario" onClick={() => mudar('rec', 'sem_dono')}>
                        Ver as sem dono
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <ul className="caixa-lista">
                      {visiveis.map((c) => <LinhaDaCaixa key={c.id} c={c} aberta={c.id === id} decorrido={decorrido} />)}
                    </ul>
                    {noTeto && (
                      <div className="caixa-teto">
                        <Aviso tipo="info">
                          Mostrando as {LIMITE_DA_CAIXA} primeiras conversas desta lista — a prioridade e a espera
                          primeiro; depois, as mais recentes. Use a busca ou os filtros para achar as mais antigas.
                        </Aviso>
                      </div>
                    )}
                  </>
                )}
          </section>
        )}

        {id ? (
          <Conversa id={id} eu={euId} diretoria={pode('comercial.diretoria')}
            lateralNaFolha={lateralNaFolha} celular={celular}
            aoAbrirLateral={() => setFolha(true)} aoMudar={() => void buscar()} aoCarregar={setDetalhe}
            versao={versaoDoDetalhe} />
        ) : !celular && (
          <section className="caixa-coluna caixa-nenhuma" aria-label="Conversa">
            <p><strong>Escolha uma conversa</strong></p>
            <p className="sub">A conversa abre aqui, com o cliente e a oportunidade ao lado.</p>
          </section>
        )}

        {tresColunas && detalhe && detalhe.conversa.id === id && <PainelDaConversa d={detalhe} aoMudar={painelMudou} />}
      </div>

      {lateralNaFolha && detalhe && detalhe.conversa.id === id && (
        <FolhaDeBaixo aberta={folha} titulo="Cliente e oportunidade" classe="folha-da-conversa" aoFechar={() => setFolha(false)}>
          <PainelDaConversa d={detalhe} aoMudar={painelMudou} />
        </FolhaDeBaixo>
      )}

      <JanelaNovaConversa aberta={novaConversa} inicial={inicialDaNova}
        aoFechar={() => { setNovaConversa(false); setInicialDaNova(undefined); }}
        aoAbrir={(conversaId, rascunho) => {
          setNovaConversa(false);
          setInicialDaNova(undefined);
          void buscar();
          navegar(`/comercial/conversa/${conversaId}`, rascunho ? { state: { rascunho } } : undefined);
        }} />
      <JanelaDaLigacao aberta={ligacao} aoFechar={() => setLigacao(false)}
        aoGravar={(r) => {
          setLigacao(false);
          void buscar();
          if (r.conversaId) navegar(`/comercial/conversa/${r.conversaId}`);
          if (r.chamar) { setInicialDaNova(r.chamar); setNovaConversa(true); }
        }} />
    </div>
  );
}
